'use client';

import { IActiveNavConfig } from '@/api/types/blog';
import Image from '@/components/Image';
import MagicJump, { getJumpUrl, JumpTarget } from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { BLOG_MARK_LIST } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Arrow_down } from '@zeelool/icon';
import React, { useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTranslations } from '@/i18n/use-translations';
// 神策参数类型
interface SensorParams {
  mkt_name: string;
  pits_index: number;
  pits_jump_type: string;
  pits_jump_content: string;
}

interface TypeBannerProps {
  activeNav: IActiveNavConfig;
  sdBlogPageMktExp?: (params: SensorParams) => void;
  sdBlogPageMktClick?: (params: SensorParams) => void;
  className?: string;
}

const TypeBanner: React.FC<TypeBannerProps> = ({
  activeNav,
  sdBlogPageMktExp = () => {},
  sdBlogPageMktClick = () => {},
  className = '',
}) => {
  const t = useTranslations('blog.components');
  const blogT = useTranslations('blog');
  const { deviceType } = useContext(ResponsiveContext);
  // 使用useInView处理曝光事件
  const [bannerRef, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  // 计算banner跳转目标
  const bannerJumpTarget = useMemo(() => {
    if (activeNav?.jump_target) {
      return activeNav.jump_target;
    }
    if (activeNav?.mark === BLOG_MARK_LIST.ALL_BLOGS) {
      return {};
    }
    return {
      jump_type: 4,
      Jump_target: `/blog/${activeNav?.another_name}`,
    };
  }, [activeNav?.jump_target, activeNav?.mark, activeNav?.another_name]);

  // 计算神策参数
  const sensorParams = useMemo((): SensorParams => {
    return {
      mkt_name: '分类banner',
      pits_index: 0,
      pits_jump_type: `${bannerJumpTarget.jump_type || ''}`,
      pits_jump_content: `${bannerJumpTarget.Jump_target || ''}`,
    };
  }, [bannerJumpTarget]);

  const bannerJumpUrl = useMemo(
    () => getJumpUrl(bannerJumpTarget as JumpTarget),
    [bannerJumpTarget],
  );
  const isBannerLink =
    Boolean(bannerJumpUrl) && [1, 2, 3, 4, 8].includes(Number(bannerJumpTarget?.jump_type));
  const bannerName = activeNav?.name?.trim() || blogT('blogCenter');

  // 处理曝光事件
  useEffect(() => {
    if (inView) {
      sdBlogPageMktExp(sensorParams);
    }
  }, [inView]);

  // 点击事件处理
  const handleBeforeJump = () => {
    sdBlogPageMktClick(sensorParams);
  };

  if (!activeNav) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      className={`type-banner ${className} ${deviceType === EPlatform.WEB ? 'flex h-[25.83vw]' : 'pt-36 px-14'}`}
    >
      {/* Banner 图片区域 */}
      <MagicJump
        prefetch={false}
        item={bannerJumpTarget as JumpTarget}
        className={`blog-banner relative block ${deviceType === EPlatform.WEB ? 'h-[25.83vw] w-[59%]' : ' h-auto w-full'}`}
        onBeforeJump={handleBeforeJump}
        aria-label={isBannerLink ? bannerName : undefined}
        openInNewTab
      >
        {activeNav.banner_image && (
          <Image
            src={activeNav.banner_image}
            alt={isBannerLink ? '' : bannerName}
            width={800}
            height={600}
            className="w-full h-full object-cover block"
          />
        )}
      </MagicJump>

      {/* Banner 文本内容 */}
      <div
        className={`
          type-banner-text box-border flex flex-col bg-white
          ${deviceType === EPlatform.WEB ? 'w-[41%] px-48 justify-center h-[25.83vw]' : 'w-full px-14 py-14'}
        `}
      >
        {/* 动态标题 */}
        <h2
          className={`font-bb text-primary-black ${deviceType === EPlatform.WEB ? 'text-32' : 'text-16 '}`}
        >
          {activeNav.name}
        </h2>

        {/* 描述文本 */}
        {activeNav.description && (
          <p
            className={`
              description mt-12 font-ir  text-primary-black
              ${deviceType === EPlatform.WEB ? 'text-14 leading-[1.5] text-ellipsis line-clamp-2' : 'text-[13px] leading-20'}
            `}
          >
            {activeNav.description}
          </p>
        )}

        {/* Read More 链接 */}
        {bannerJumpTarget.Jump_target && (
          <MagicJump
            prefetch={false}
            item={bannerJumpTarget as JumpTarget}
            openInNewTab
            className={`
              read-more w-fit flex  cursor-pointer
              ${deviceType === EPlatform.WEB ? 'mt-[2.08vw] justify-center' : 'mt-12 items-center self-end'}
            `}
            onBeforeJump={handleBeforeJump}
          >
            <span
              className={`underline font-bs text-primary-black ${deviceType === EPlatform.WEB ? 'text-14' : 'text-12'}`}
            >
              {t('readMore')}
              <span className="sr-only"> {bannerName}</span>
            </span>
            <i className={`ml-8 ${deviceType === EPlatform.WEB ? 'text-16' : 'text-12'}`}>
              <Arrow_down aria-hidden className="rotate-270" />
            </i>
          </MagicJump>
        )}
      </div>
    </div>
  );
};

export default TypeBanner;
